import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export type PreMeetStatus = "drafted" | "sent" | "acknowledged" | "met" | "canceled";

export interface TrustedShareDecision {
  required: boolean;
  waived: boolean;
  share_enabled: boolean;
  trusted_count: number;
  venue_is_curated: boolean;
  venue_same_city: boolean;
  venue_has_safety_doc: boolean;
  venue_id: string | null;
  venue_name: string | null;
  venue_city: string | null;
  draft_city: string | null;
  decided_at: string;
}

export interface PreMeetChecklist {
  id: string;
  user_id: string;
  match_user_id: string | null;
  meet_at: string | null;
  meet_place_label: string | null;
  meet_lat: number | null;
  meet_lng: number | null;
  shared_with_trusted: boolean;
  has_safe_word: boolean;
  has_transport: boolean;
  has_id_verified: boolean;
  has_video_call_done: boolean;
  status: PreMeetStatus;
  trusted_share_decision: TrustedShareDecision | null;
  created_at: string;
}

export function usePreMeetChecklists() {
  const { user } = useAuth();
  const [items, setItems] = useState<PreMeetChecklist[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) return;
    const { data } = await (supabase as any)
      .from("pre_meet_checklists")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    setItems((data ?? []) as unknown as PreMeetChecklist[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const upsert = useCallback(
    async (patch: Partial<PreMeetChecklist> & { id?: string }) => {
      if (!user) return { error: "not authenticated" };
      const row = { user_id: user.id, ...patch };
      const { data, error } = await (supabase as any)
        .from("pre_meet_checklists")
        .upsert(row)
        .select("id")
        .maybeSingle();
      if (error) return { error: error.message };

      // Schedule (or reschedule) T-1h and T-10m reminder notifications.
      // Best-effort: failures here don't block the save.
      const checklistId = patch.id ?? data?.id;
      if (checklistId) {
        try {
          await supabase.rpc("fn_schedule_premeet_reminders", {
            _checklist_id: checklistId,
          });
        } catch (e) {
          console.warn("[pre-meet] reminder scheduling failed", e);
        }
      }

      await refresh();
      return { error: null };
    },
    [user, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      const { error } = await (supabase as any).from("pre_meet_checklists").delete().eq("id", id);
      if (!error) await refresh();
      return { error: error?.message ?? null };
    },
    [refresh],
  );

  return { items, loading, upsert, remove, refresh };
}
